import * as React from 'react'
import { cn } from '@/lib/utils'
import { supabase } from '@/lib/supabase'
import { BaseFileUpload } from './BaseFileUpload'
import { RichTextEditor } from './RichTextEditor'
import { BottomDrawer } from './BottomDrawer'
import { createPortal } from 'react-dom'
import { computeEmojiPickerPlacement, type EmojiPickerAlign } from './emojiPickerPlacement'
import { buildMentionHref, normalizeMentionDisplayName } from '@/shared/utils/mentions'
import { MentionAutocomplete, type MentionUser } from './MentionAutocomplete'
import {
  AtSign,
  Bold,
  Code,
  CodeXml,
  Italic,
  Plus,
  Strikethrough,
  Underline,
  Link2,
  List,
  ListOrdered,
  Quote,
  SendHorizontal,
  Smile,
  X,
} from 'lucide-react'

export type MessageEditorTool =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strikethrough'
  | 'link'
  | 'bulletedList'
  | 'numberedList'
  | 'blockQuote'
  | 'code'
  | 'codeBlock'

export type MessageEditorVariant = 'default' | 'slack'

export type MessageEditorProps = {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  variant?: MessageEditorVariant
  enableMentions?: boolean
  mentionLimit?: number
  onPickFiles?: (files: File[]) => void
  pickFilesAccept?: string
  pickFilesMultiple?: boolean
  /**
   * Altura aproximada da área de digitação.
   * Para compositores curtos (chat), use 100–160.
   */
  height?: number
  /**
   * Quando true, Enter envia a mensagem (onSubmit)
   * e Shift+Enter apenas insere nova linha no editor.
   */
  sendOnEnter?: boolean
  onSubmit?: () => void
  submitDisabled?: boolean
  /**
   * Conjunto de ferramentas exibidas na toolbar customizada
   * (ordem respeitada).
   */
  toolbar?: MessageEditorTool[]
  /**
   * Slot opcional para renderizar ações à direita
   * da barra de ferramentas inferior (ex.: botão Enviar).
   */
  footerRightSlot?: React.ReactNode
  /**
   * Quando true, exibe um botão de envio
   * alinhado à direita da barra inferior.
   * Requer `onSubmit` definido.
   */
  showSubmitButton?: boolean
  submitLabel?: string
  onPasteFiles?: (files: File[]) => void
  /**
   * Quando true, remove qualquer imagem inline (<img> / <figure class="image">)
   * do HTML retornado pelo editor, mantendo apenas texto e formatação básica.
   * Útil para chats onde as imagens devem aparecer apenas como anexos externos.
   */
  stripImages?: boolean
  /**
   * Renderizador opcional do seletor de emojis (UI).
   * Quando fornecido, o botāo de emoji da variante `slack` passa a abrir este picker.
   * O `onSelect` deve ser chamado com o emoji Unicode (ou token de emoji custom).
   */
  renderEmojiPicker?: (params: {
    onSelect: (emoji: string) => void
    onClose: () => void
    className?: string
  }) => React.ReactNode
}

type MentionEditorContext = {
  query: string
  range: any
}

const WORD_CHAR_REGEX = /[\p{L}\p{N}_]/u

function forEachCkIteratorValue(iter: any, cb: (value: any) => void) {
  if (!iter || !cb) return

  const iteratorSymbol = typeof Symbol === 'function' ? (Symbol as any).iterator : null
  if (iteratorSymbol && typeof iter?.[iteratorSymbol] === 'function') {
    try {
      for (const value of iter) cb(value)
      return
    } catch {
      // fall through para tentativa manual via next()
    }
  }

  const nextFn = iter?.next
  if (typeof nextFn !== 'function') return

  try {
    let step = nextFn.call(iter)
    while (step) {
      if (typeof step === 'object' && step !== null && 'done' in step) {
        if ((step as any).done) break
        cb((step as any).value)
      } else {
        cb(step)
      }
      step = nextFn.call(iter)
    }
  } catch {
    // ignore
  }
}

function collectModelTextNodes(node: any, out: any[]) {
  if (!node || !out) return
  try {
    const iter = node.getChildren?.()
    if (!iter) return
    forEachCkIteratorValue(iter, (child) => {
      if (child?.is?.('text')) out.push(child)
      else if (child?.getChildren) collectModelTextNodes(child, out)
    })
  } catch {
    // ignore
  }
}

function getTextFromModelRange(range: any): string {
  if (!range) return ''

  try {
    const walker = range.getWalker?.({ ignoreElementEnd: true })
    if (walker) {
      let out = ''
      forEachCkIteratorValue(walker, (step) => {
        const item = step?.item
        const isText =
          item?.is?.('text') || item?.is?.('textProxy') || typeof item?.data === 'string'
        if (isText) out += String(item?.data || '')
      })
      if (out) return out
    }
  } catch {
    // ignore
  }

  try {
    const items = range.getItems?.()
    if (items) {
      let out = ''
      forEachCkIteratorValue(items, (item) => {
        const isText =
          item?.is?.('text') || item?.is?.('textProxy') || typeof item?.data === 'string'
        if (isText) out += String(item?.data || '')
      })
      return out
    }
  } catch {
    // ignore
  }

  return ''
}

function getMentionContextFromEditor(editor: any): MentionEditorContext | null {
  try {
    const selection = editor?.model?.document?.selection
    const pos = selection?.getFirstPosition?.()
    if (!pos) return null

    let container: any = pos.parent
    if (container?.is?.('text')) container = container.parent

    while (container?.parent) {
      if (
        container?.is?.('element', 'paragraph') ||
        container?.is?.('element', 'listItem') ||
        container?.is?.('element', 'blockQuote')
      ) {
        break
      }
      if (!container.parent || container.parent?.is?.('rootElement')) break
      container = container.parent
    }

    const nodes: any[] = []
    collectModelTextNodes(container, nodes)
    if (nodes.length === 0) return null

    const ranges: Array<{ node: any; start: number; end: number; text: string }> = []
    let offset = 0
    for (const n of nodes) {
      const t = String(n?.data || '')
      const start = offset
      const end = start + t.length
      ranges.push({ node: n, start, end, text: t })
      offset = end
    }

    const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n))

    const resolveCaretTextNode = (): { node: any; offsetInNode: number } | null => {
      try {
        const parent = pos.parent
        if (parent?.is?.('text')) return { node: parent, offsetInNode: pos.offset }
        if (parent?.is?.('textProxy')) {
          const base = parent.textNode || parent
          const baseOffset = typeof parent.offsetInText === 'number' ? parent.offsetInText : 0
          return { node: base, offsetInNode: baseOffset + pos.offset }
        }
      } catch {
        // ignore
      }

      try {
        const beforeNode = pos.nodeBefore
        if (beforeNode?.is?.('text')) {
          return { node: beforeNode, offsetInNode: String(beforeNode?.data || '').length }
        }
      } catch {
        // ignore
      }

      try {
        const afterNode = pos.nodeAfter
        if (afterNode?.is?.('text')) {
          return { node: afterNode, offsetInNode: 0 }
        }
      } catch {
        // ignore
      }

      return null
    }

    const caret = resolveCaretTextNode()
    let before = ''
    if (caret?.node) {
      let found = false
      for (const n of nodes) {
        const t = String(n?.data || '')
        if (n === caret.node) {
          before += t.slice(0, clamp(caret.offsetInNode, 0, t.length))
          found = true
          break
        }
        before += t
      }
      if (!found) before = ''
    }

    if (!before) {
      const startOfContainer = editor.model.createPositionAt(container, 0)
      const rangeToCaret = editor.model.createRange(startOfContainer, pos)
      before = getTextFromModelRange(rangeToCaret)
    }

    if (!before.includes('@')) return null
    const caretOffset = clamp(before.length, 0, offset)
    const at = before.lastIndexOf('@')
    if (at < 0) return null

    const prev = at > 0 ? before[at - 1] : ''
    if (prev && WORD_CHAR_REGEX.test(prev)) return null

    const query = before.slice(at + 1)
    if (query.length > 50) return null
    if (/\s/.test(query)) return null

    const positionAt = (abs: number) => {
      const r = ranges.find((it) => abs <= it.end) || ranges[ranges.length - 1]
      const inside = Math.max(0, Math.min(abs - r.start, r.end - r.start))
      return editor.model.createPositionAt(r.node, inside)
    }

    const startPos = positionAt(at)
    const endPos = positionAt(caretOffset)
    const range = editor.model.createRange(startPos, endPos)

    return { query, range }
  } catch {
    return null
  }
}

const DEFAULT_MESSAGE_TOOLBAR: MessageEditorTool[] = [
  'bold',
  'italic',
  'underline',
  'link',
  'bulletedList',
  'numberedList',
  'blockQuote',
]

type ToolConfig = {
  command: string
  icon: React.ComponentType<{ className?: string }>
  label: string
}

const TOOL_CONFIG: Record<MessageEditorTool, ToolConfig> = {
  bold: { command: 'bold', icon: Bold, label: 'Negrito' },
  italic: { command: 'italic', icon: Italic, label: 'Itálico' },
  underline: { command: 'underline', icon: Underline, label: 'Sublinhado' },
  strikethrough: { command: 'strikethrough', icon: Strikethrough, label: 'Riscado' },
  link: { command: 'link', icon: Link2, label: 'Link' },
  bulletedList: { command: 'bulletedList', icon: List, label: 'Lista' },
  numberedList: { command: 'numberedList', icon: ListOrdered, label: 'Lista numerada' },
  blockQuote: { command: 'blockQuote', icon: Quote, label: 'Citação' },
  code: { command: 'code', icon: Code, label: 'Código' },
  codeBlock: { command: 'codeBlock', icon: CodeXml, label: 'Bloco de código' },
}

const SLACK_FORMAT_TOOLS: MessageEditorTool[] = [
  'bold',
  'italic',
  'underline',
  'strikethrough',
  'numberedList',
  'bulletedList',
]

export function MessageEditor({
  value,
  onChange,
  placeholder,
  disabled,
  className,
  variant = 'default',
  enableMentions = false,
  mentionLimit = 20,
  height,
  sendOnEnter,
  onSubmit,
  submitDisabled,
  toolbar,
  footerRightSlot,
  showSubmitButton,
  submitLabel,
  onPasteFiles,
  onPickFiles,
  pickFilesAccept,
  pickFilesMultiple,
  stripImages,
  renderEmojiPicker,
}: MessageEditorProps) {
  const composerContainerRef = React.useRef<HTMLDivElement | null>(null)
  const editorRef = React.useRef<any | null>(null)
  const [isEditorReady, setIsEditorReady] = React.useState(false)
  const [slackFormatOpen, setSlackFormatOpen] = React.useState(false)
  const [emojiPickerOpen, setEmojiPickerOpen] = React.useState(false)
  const [emojiPickerAlign, setEmojiPickerAlign] = React.useState<EmojiPickerAlign>('left')
  const emojiButtonRef = React.useRef<HTMLButtonElement | null>(null)
  const emojiPopoverRef = React.useRef<HTMLDivElement | null>(null)
  const mentionButtonRef = React.useRef<HTMLButtonElement | null>(null)
  const mentionPopoverRef = React.useRef<HTMLDivElement | null>(null)
  const [emojiPortal, setEmojiPortal] = React.useState<HTMLElement | null>(null)
  const [emojiPickerPosition, setEmojiPickerPosition] = React.useState<
    Partial<Record<'top' | 'left' | 'right' | 'bottom', number>> | null
  >(null)
  const [mentionOpen, setMentionOpen] = React.useState(false)
  const [mentionQuery, setMentionQuery] = React.useState('')
  const [mentionItems, setMentionItems] = React.useState<MentionUser[]>([])
  const [mentionLoading, setMentionLoading] = React.useState(false)
  const [mentionLoadingMore, setMentionLoadingMore] = React.useState(false)
  const [mentionHasMore, setMentionHasMore] = React.useState(false)
  const [mentionError, setMentionError] = React.useState<string | null>(null)
  const [mentionActiveIndex, setMentionActiveIndex] = React.useState(0)
  const [mentionPickerAlign, setMentionPickerAlign] =
    React.useState<EmojiPickerAlign>('left')
  const [mentionPickerPosition, setMentionPickerPosition] = React.useState<
    Partial<Record<'top' | 'left' | 'right' | 'bottom', number>> | null
  >(null)
  const mentionOpenRef = React.useRef(false)
  const mentionQueryRef = React.useRef('')
  const mentionItemsRef = React.useRef<MentionUser[]>([])
  const mentionActiveIndexRef = React.useRef(0)
  const mentionRangeRef = React.useRef<any | null>(null)
  const mentionLoadMoreInFlightRef = React.useRef(false)
  const valueRef = React.useRef<string>(value)
  const resolvedHeight = height ?? (variant === 'slack' ? 44 : 100)
  const mentionsEnabled = enableMentions && variant === 'slack'

  const [isLg, setIsLg] = React.useState(() => {
    if (typeof window === 'undefined') return true
    try {
      return window.matchMedia('(min-width: 1024px)').matches
    } catch {
      return true
    }
  })

  React.useEffect(() => {
    valueRef.current = value
  }, [value])

  React.useEffect(() => {
    mentionOpenRef.current = mentionOpen
  }, [mentionOpen])

  React.useEffect(() => {
    mentionQueryRef.current = mentionQuery
  }, [mentionQuery])

  React.useEffect(() => {
    mentionItemsRef.current = mentionItems
  }, [mentionItems])

  React.useEffect(() => {
    mentionActiveIndexRef.current = mentionActiveIndex
  }, [mentionActiveIndex])

  React.useEffect(() => {
    if (typeof document === 'undefined') return
    setEmojiPortal(document.body)
  }, [])

  React.useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      const mq = window.matchMedia('(min-width: 1024px)')
      const handle = () => setIsLg(mq.matches)
      handle()
      mq.addEventListener('change', handle)
      return () => mq.removeEventListener('change', handle)
    } catch {
      // ignore
    }
  }, [])

  React.useEffect(() => {
    if (!emojiPickerOpen) return
    if (!isLg) return

    const onDoc = (ev: MouseEvent) => {
      const target = ev.target
      if (!(target instanceof Node)) return
      const button = emojiButtonRef.current
      if (button && button.contains(target)) return
      const popover = emojiPopoverRef.current
      if (popover && popover.contains(target)) return
      setEmojiPickerOpen(false)
    }

    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [emojiPickerOpen, isLg])

  React.useEffect(() => {
    if (!mentionOpen) return

    const onDoc = (ev: MouseEvent) => {
      const target = ev.target
      if (!(target instanceof Node)) return
      const button = mentionButtonRef.current
      if (button && button.contains(target)) return
      const popover = mentionPopoverRef.current
      if (popover && popover.contains(target)) return
      setMentionOpen(false)
      setMentionQuery('')
      setMentionItems([])
      setMentionError(null)
      setMentionActiveIndex(0)
      mentionRangeRef.current = null
    }

    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [mentionOpen])

  const updateEmojiPickerLayout = React.useCallback(() => {
    if (typeof window === 'undefined') return
    const buttonEl = emojiButtonRef.current
    if (!buttonEl) return

    const buttonRect = buttonEl.getBoundingClientRect()
    const { placement, align } = computeEmojiPickerPlacement({
      buttonRect,
      containerRect: null,
      estimatedPickerHeight: 520,
      pickerWidth: 320,
    })

    setEmojiPickerAlign(align)

    const viewportW = window.innerWidth || document.documentElement.clientWidth || 0
    const viewportH = window.innerHeight || document.documentElement.clientHeight || 0
    const gap = 8

    const pos: Partial<Record<'top' | 'left' | 'right' | 'bottom', number>> = {}

    if (placement === 'top') {
      pos.bottom = Math.max(gap, viewportH - buttonRect.top + gap)
    } else {
      pos.top = Math.max(gap, buttonRect.bottom + gap)
    }

    if (align === 'right') {
      pos.right = Math.max(gap, viewportW - buttonRect.right)
    } else {
      pos.left = Math.max(gap, buttonRect.left)
    }

    setEmojiPickerPosition(pos)
  }, [])

  const updateMentionPickerLayout = React.useCallback(() => {
    if (typeof window === 'undefined') return
    const anchorEl = composerContainerRef.current || mentionButtonRef.current
    if (!anchorEl) return

    const anchorRect = anchorEl.getBoundingClientRect()

    const viewportW = window.innerWidth || document.documentElement.clientWidth || 0
    const viewportH = window.innerHeight || document.documentElement.clientHeight || 0
    const gap = 8
    const estimatedPickerHeight = 340

    const spaceAbove = anchorRect.top
    const spaceBelow = viewportH - anchorRect.bottom
    const placement =
      spaceAbove >= estimatedPickerHeight + gap || spaceAbove >= spaceBelow ? 'top' : 'bottom'

    const pos: Partial<Record<'top' | 'left' | 'right' | 'bottom', number>> = {}

    if (placement === 'top') {
      pos.bottom = Math.max(gap, viewportH - anchorRect.top + gap)
    } else {
      pos.top = Math.max(gap, anchorRect.bottom + gap)
    }

    pos.left = Math.max(gap, anchorRect.left)
    pos.right = Math.max(gap, viewportW - anchorRect.right)

    setMentionPickerAlign('left')

    setMentionPickerPosition(pos)
  }, [])

  React.useEffect(() => {
    if (!emojiPickerOpen) return
    if (!isLg) return

    updateEmojiPickerLayout()

    const handle = () => updateEmojiPickerLayout()
    window.addEventListener('resize', handle)
    window.addEventListener('scroll', handle, true)
    return () => {
      window.removeEventListener('resize', handle)
      window.removeEventListener('scroll', handle, true)
    }
  }, [emojiPickerOpen, isLg, updateEmojiPickerLayout])

  React.useEffect(() => {
    if (!mentionOpen) return
    updateMentionPickerLayout()

    const handle = () => updateMentionPickerLayout()
    window.addEventListener('resize', handle)
    window.addEventListener('scroll', handle, true)
    return () => {
      window.removeEventListener('resize', handle)
      window.removeEventListener('scroll', handle, true)
    }
  }, [mentionOpen, updateMentionPickerLayout])

  React.useEffect(() => {
    if (!slackFormatOpen) return
    if (!emojiPickerOpen) return
    setEmojiPickerOpen(false)
  }, [emojiPickerOpen, slackFormatOpen])

  React.useEffect(() => {
    if (!slackFormatOpen) return
    if (!mentionOpen) return
    setMentionOpen(false)
    setMentionQuery('')
    setMentionItems([])
    setMentionError(null)
    setMentionActiveIndex(0)
    mentionRangeRef.current = null
  }, [mentionOpen, slackFormatOpen])

  const handleEnterPress = React.useCallback(() => {
    if (!sendOnEnter) return
    if (disabled) return
    if (submitDisabled) return
    if (!onSubmit) return
    if (mentionOpenRef.current) return
    onSubmit()
  }, [disabled, onSubmit, sendOnEnter, submitDisabled])

  const toolbarRequested = toolbar === undefined ? DEFAULT_MESSAGE_TOOLBAR : toolbar
  const tools = toolbarRequested.filter((tool) => !!TOOL_CONFIG[tool])
  const editorTools = variant === 'slack' ? SLACK_FORMAT_TOOLS : tools
  const shouldShowSubmitButton = !!(showSubmitButton || (sendOnEnter && onSubmit))
  const resolvedSubmitDisabled = !!(disabled || submitDisabled)

  const isToolSupported = React.useCallback((tool: MessageEditorTool) => {
    const editor = editorRef.current
    if (!editor) return false
    const cfg = TOOL_CONFIG[tool]
    if (!cfg?.command) return false
    try {
      return !!editor.commands?.get?.(cfg.command)
    } catch {
      return false
    }
  }, [])

  const handleToolClick = React.useCallback((tool: MessageEditorTool) => {
    if (!isEditorReady) return
    const editor = editorRef.current
    if (!editor) return
    const cfg = TOOL_CONFIG[tool]
    if (!cfg) return
    if (!isToolSupported(tool)) return
    try {
      editor.editing?.view?.focus()
      editor.execute(cfg.command)
      editor.editing?.view?.focus()
    } catch {
      // Se o comando não existir neste build do CKEditor, ignoramos em silêncio.
    }
  }, [isEditorReady, isToolSupported])

  const sanitizeHtmlForMessage = React.useCallback(
    (html: string) => {
      if (!html) return html

      // Remove figuras de imagem completas (CKEditor costuma envolver em <figure class="image">)
      let cleaned = html.replace(
        /<figure[^>]*class=["'][^"']*image[^"']*["'][^>]*>[\s\S]*?<\/figure>/gi,
        '',
      )

      // Remove qualquer <img> residual
      cleaned = cleaned.replace(/<img[^>]*>/gi, '')

       // Remove URLs de imagem (ex.: https://.../arquivo.png) do conteúdo textual,
       // deixando que o anexo visual represente a imagem.
       const imgUrlRegex =
         /(https?:\/\/[^\s<]+?\.(?:png|jpe?g|gif|webp))(?![^\s<])/gi
       cleaned = cleaned.replace(imgUrlRegex, '')
       cleaned = cleaned.replace(/\s{2,}/g, ' ')

      return cleaned
    },
    [],
  )

  const handleChange = React.useCallback(
    (html: string) => {
      const next = stripImages ? sanitizeHtmlForMessage(html) : html
      onChange(next)
    },
    [onChange, sanitizeHtmlForMessage, stripImages],
  )

  const insertText = React.useCallback(
    (text: string) => {
      const next = String(text || '')
      if (!next.trim()) return

      const editor = editorRef.current
      if (editor?.model?.change && editor?.model?.insertContent) {
        try {
          editor.editing?.view?.focus()
        } catch {
          // ignore
        }

        try {
          editor.model.change((writer: any) => {
            const node = writer.createText(next)
            editor.model.insertContent(node)
          })
          return
        } catch {
          // ignore
        }
      }

      onChange(`${String(valueRef.current || '')}${next}`)
    },
    [onChange],
  )

  const closeMention = React.useCallback(() => {
    setMentionOpen(false)
    setMentionQuery('')
    setMentionItems([])
    setMentionLoading(false)
    setMentionLoadingMore(false)
    setMentionHasMore(false)
    setMentionError(null)
    setMentionActiveIndex(0)
    mentionLoadMoreInFlightRef.current = false
    mentionRangeRef.current = null
  }, [])

  const handleMentionQueryChange = React.useCallback((q: string) => {
    setMentionQuery(q)
    setMentionActiveIndex(0)
  }, [])

  const moveMentionActiveIndex = React.useCallback((delta: number) => {
    setMentionActiveIndex((current) => {
      const max = Math.max(0, mentionItemsRef.current.length - 1)
      return Math.max(0, Math.min(max, current + delta))
    })
  }, [])

  const mentionSyncTimeoutRef = React.useRef<number | null>(null)

  const syncMentionFromEditor = React.useCallback(() => {
    if (!mentionsEnabled) return
    if (!isEditorReady) return
    if (disabled) return
    if (slackFormatOpen) {
      if (mentionOpenRef.current) closeMention()
      return
    }

    const editor = editorRef.current
    if (!editor) return

    const ctx = getMentionContextFromEditor(editor)
    if (!ctx) {
      mentionRangeRef.current = null
      if (mentionOpenRef.current) closeMention()
      return
    }

    mentionRangeRef.current = ctx.range
    if (emojiPickerOpen) setEmojiPickerOpen(false)
    setMentionQuery(ctx.query)
    if (!mentionOpenRef.current) {
      setMentionItems([])
      setMentionError(null)
      setMentionActiveIndex(0)
    }
    setMentionOpen(true)
  }, [
    closeMention,
    disabled,
    emojiPickerOpen,
    isEditorReady,
    mentionsEnabled,
    slackFormatOpen,
  ])

  const scheduleMentionSync = React.useCallback(() => {
    if (typeof window === 'undefined') return
    if (mentionSyncTimeoutRef.current !== null) return
    mentionSyncTimeoutRef.current = window.setTimeout(() => {
      mentionSyncTimeoutRef.current = null
      syncMentionFromEditor()
    }, 0)
  }, [syncMentionFromEditor])

  React.useEffect(() => {
    return () => {
      if (mentionSyncTimeoutRef.current !== null) {
        clearTimeout(mentionSyncTimeoutRef.current)
        mentionSyncTimeoutRef.current = null
      }
    }
  }, [])

  const handleEmojiButtonClick = React.useCallback(() => {
    if (disabled) return
    if (!renderEmojiPicker) return
    if (!isEditorReady) return

    const nextOpen = !emojiPickerOpen
    if (nextOpen) closeMention()
    if (nextOpen && isLg) {
      updateEmojiPickerLayout()
    }
    setEmojiPickerOpen(nextOpen)
  }, [
    closeMention,
    disabled,
    emojiPickerOpen,
    isEditorReady,
    isLg,
    renderEmojiPicker,
    updateEmojiPickerLayout,
  ])

  React.useEffect(() => {
    if (!mentionsEnabled) return
    if (!mentionOpen) return

    let cancelled = false
    setMentionLoading(true)
    setMentionLoadingMore(false)
    setMentionHasMore(false)
    setMentionError(null)
    const requestedQuery = mentionQuery
    mentionLoadMoreInFlightRef.current = false

    const t = setTimeout(async () => {
      try {
        const { data, error } = await supabase.rpc('search_mentionable_users', {
          p_query: requestedQuery,
          p_limit: mentionLimit,
          p_offset: 0,
        } as any)
        if (cancelled) return
        if (error) throw error
        const items = ((data as any[]) || []).map((row) => ({
          id: String(row?.id || ''),
          full_name: row?.full_name ?? null,
          avatar_url: row?.avatar_url ?? null,
        })) as MentionUser[]
        const nextItems = items.filter((u) => !!u.id)
        setMentionItems(nextItems)
        setMentionActiveIndex(0)
        setMentionHasMore(nextItems.length === mentionLimit)
      } catch (e: any) {
        if (cancelled) return
        setMentionItems([])
        setMentionHasMore(false)
        setMentionError(e?.message || 'Falha ao buscar usuários')
      } finally {
        if (!cancelled) setMentionLoading(false)
      }
    }, 140)

    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [mentionsEnabled, mentionLimit, mentionOpen, mentionQuery])

  const loadMoreMentions = React.useCallback(async () => {
    if (!mentionsEnabled) return
    if (!mentionOpenRef.current) return
    if (mentionLoading || mentionLoadingMore) return
    if (!mentionHasMore) return

    const query = mentionQueryRef.current
    const offset = mentionItemsRef.current.length
    if (!offset) return
    if (mentionLoadMoreInFlightRef.current) return
    mentionLoadMoreInFlightRef.current = true

    setMentionLoadingMore(true)
    setMentionError(null)

    try {
      const { data, error } = await supabase.rpc('search_mentionable_users', {
        p_query: query,
        p_limit: mentionLimit,
        p_offset: offset,
      } as any)
      if (error) throw error

      const items = ((data as any[]) || []).map((row) => ({
        id: String(row?.id || ''),
        full_name: row?.full_name ?? null,
        avatar_url: row?.avatar_url ?? null,
      })) as MentionUser[]
      const nextItems = items.filter((u) => !!u.id)

      if (!mentionOpenRef.current) return
      if (mentionQueryRef.current !== query) return

      const existingIds = new Set(mentionItemsRef.current.map((u) => u.id))
      const uniqueNew = nextItems.filter((u) => !!u?.id && !existingIds.has(u.id))
      if (uniqueNew.length === 0) {
        setMentionHasMore(false)
        return
      }

      setMentionItems((current) => {
        const existing = new Set(current.map((u) => u.id))
        const merged = [...current]
        for (const item of uniqueNew) {
          if (!item?.id) continue
          if (existing.has(item.id)) continue
          existing.add(item.id)
          merged.push(item)
        }
        return merged
      })

      setMentionHasMore(nextItems.length === mentionLimit && uniqueNew.length > 0)
    } catch (e: any) {
      if (!mentionOpenRef.current) return
      if (mentionQueryRef.current !== query) return
      setMentionError(e?.message || 'Falha ao buscar usuarios')
      setMentionHasMore(false)
    } finally {
      mentionLoadMoreInFlightRef.current = false
      setMentionLoadingMore(false)
    }
  }, [
    mentionHasMore,
    mentionLimit,
    mentionLoading,
    mentionLoadingMore,
    mentionsEnabled,
  ])

  const insertMention = React.useCallback(
    (user: MentionUser) => {
      const editor = editorRef.current
      const label = normalizeMentionDisplayName(user.full_name)
      const href = buildMentionHref(user.id)

      if (!editor?.model?.change || !editor?.model?.insertContent) {
        insertText(`@${label} `)
        closeMention()
        return
      }

      try {
        editor.editing?.view?.focus()
      } catch {
        // ignore
      }

      const rangeFromRef = mentionRangeRef.current || null
      const rangeFromCtx = getMentionContextFromEditor(editor)?.range || null

      try {
        editor.model.change((writer: any) => {
          const trySetSelection = (candidate: any) => {
            if (!candidate) return false
            try {
              writer.setSelection(candidate)
              return true
            } catch {
              try {
                writer.setSelection(candidate?.start || candidate)
                return true
              } catch {
                return false
              }
            }
          }

          trySetSelection(rangeFromRef) || trySetSelection(rangeFromCtx)

          const mentionNode = writer.createText(`@${label}`, { linkHref: href })
          editor.model.insertContent(mentionNode)

          const spaceNode = writer.createText(' ')
          editor.model.insertContent(spaceNode)
        })
      } catch {
        insertText(`@${label} `)
      } finally {
        closeMention()
      }
    },
    [closeMention, insertText],
  )

  const selectActiveMention = React.useCallback(() => {
    const items = mentionItemsRef.current
    const idx = mentionActiveIndexRef.current
    const selected = items[idx]
    if (!selected) return
    insertMention(selected)
  }, [insertMention])

  React.useEffect(() => {
    if (!mentionsEnabled) return
    if (!isEditorReady) return
    const editor = editorRef.current
    if (!editor?.model?.document?.on) return

    try {
      editor.model.document.on('change:data', scheduleMentionSync)
    } catch {
      // ignore
    }

    try { scheduleMentionSync() } catch {}

    return () => {
      try { editor.model.document.off('change:data', scheduleMentionSync) } catch {}
    }
  }, [isEditorReady, mentionsEnabled, scheduleMentionSync])

  React.useEffect(() => {
    if (!mentionsEnabled) return
    if (!isEditorReady) return
    const editor = editorRef.current
    const viewDocument = editor?.editing?.view?.document
    if (!viewDocument?.on) return

    const onKeyDown = (evt: any, data: any) => {
      const domEvent = data?.domEvent
      const key = domEvent?.key || ''

      const isAltGraph =
        (typeof domEvent?.getModifierState === 'function' &&
          domEvent.getModifierState('AltGraph')) ||
        (!!domEvent?.ctrlKey && !!domEvent?.altKey)
      const isShortcut = (!!domEvent?.metaKey || !!domEvent?.ctrlKey) && !isAltGraph
      const shouldSync =
        key === 'Backspace' || key === 'Delete' || (typeof key === 'string' && key.length === 1)

      if (!isShortcut && shouldSync) {
        scheduleMentionSync()
      }

      if (!mentionOpenRef.current) return

      if (key === 'Escape') {
        try { data.preventDefault() } catch {}
        try { evt.stop() } catch {}
        closeMention()
        return
      }

      if (key === 'ArrowDown' || key === 'Down') {
        try { data.preventDefault() } catch {}
        try { evt.stop() } catch {}
        setMentionActiveIndex((i) => {
          const max = Math.max(0, mentionItemsRef.current.length - 1)
          return Math.min(max, i + 1)
        })
        return
      }

      if (key === 'ArrowUp' || key === 'Up') {
        try { data.preventDefault() } catch {}
        try { evt.stop() } catch {}
        setMentionActiveIndex((i) => Math.max(0, i - 1))
        return
      }

      if (key === 'Enter') {
        const items = mentionItemsRef.current
        const idx = mentionActiveIndexRef.current
        const selected = items[idx]
        if (!selected) return
        try { data.preventDefault() } catch {}
        try { evt.stop() } catch {}
        insertMention(selected)
      }
    }

    try {
      viewDocument.on('keydown', onKeyDown)
    } catch {
      // ignore
    }

    return () => {
      try { viewDocument.off('keydown', onKeyDown) } catch {}
    }
  }, [
    closeMention,
    insertMention,
    isEditorReady,
    mentionsEnabled,
    scheduleMentionSync,
  ])

  const handleMentionButtonClick = React.useCallback(() => {
    if (!mentionsEnabled) return
    if (disabled) return
    if (!isEditorReady) return
    if (emojiPickerOpen) setEmojiPickerOpen(false)
    if (mentionOpenRef.current) {
      closeMention()
      return
    }
    const editor = editorRef.current
    if (!editor) return
    try { editor.editing?.view?.focus() } catch {}
    const ctx = getMentionContextFromEditor(editor)
    mentionRangeRef.current = ctx?.range || null
    setMentionQuery(ctx?.query || '')
    setMentionItems([])
    setMentionError(null)
    setMentionActiveIndex(0)
    setMentionOpen(true)
  }, [closeMention, disabled, emojiPickerOpen, isEditorReady, mentionsEnabled])

  return (
    <div
      ref={composerContainerRef}
      className={cn(
        variant === 'slack'
          ? 'rounded-2xl border border-[#272727] bg-[#111111] px-3 py-3 text-sm text-foreground shadow-sm'
          : 'rounded-lg border border-[#272727] bg-[#111111] px-2 py-1 text-sm text-foreground shadow-sm',
        'focus-within:border-[#38bdf8]/60',
        disabled && 'pointer-events-none opacity-60',
        className,
      )}
    >
      <RichTextEditor
        value={value}
        onChange={handleChange}
        height={resolvedHeight}
        placeholder={placeholder}
        toolbar={editorTools}
        onEnterPress={sendOnEnter ? handleEnterPress : undefined}
        onReady={(editor) => {
          editorRef.current = editor
          setIsEditorReady(true)
        }}
        onPasteFiles={onPasteFiles}
        className={cn('message-editor text-sm', variant === 'slack' && 'message-editor--slack')}
      />

      {variant === 'slack' ? (
        <div className="mt-2 flex items-center justify-between">
          <div className="flex min-w-0 flex-1 items-center overflow-hidden">
            <div
              className={cn(
                'flex w-[200%] items-center transition-transform duration-200 ease-out',
                slackFormatOpen ? 'translate-x-0' : '-translate-x-1/2',
              )}
            >
              <div className="flex w-1/2 items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setSlackFormatOpen(false)}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-sky-500 text-black hover:bg-sky-400"
                  aria-label="Fechar formatação"
                  tabIndex={slackFormatOpen ? 0 : -1}
                  disabled={!!disabled}
                >
                  <X className="h-5 w-5" />
                </button>

                {SLACK_FORMAT_TOOLS.map((tool) => {
                  const cfg = TOOL_CONFIG[tool]
                  if (!cfg) return null
                  const Icon = cfg.icon
                  const toolDisabled =
                    !!disabled || !isEditorReady || !isToolSupported(tool)

                  const shouldRenderSeparator =
                    tool === 'strikethrough'

                  return (
                    <React.Fragment key={tool}>
                      <button
                        type="button"
                        onClick={() => handleToolClick(tool)}
                        className={cn(
                          'inline-flex h-10 w-10 items-center justify-center rounded-full text-foreground/60 hover:bg-[#1f1f1f] hover:text-foreground',
                          toolDisabled && 'cursor-not-allowed opacity-40',
                        )}
                        aria-label={cfg.label}
                        title={cfg.label}
                        tabIndex={slackFormatOpen ? 0 : -1}
                        disabled={toolDisabled}
                      >
                        {tool === 'bold' ? (
                          <span className="text-base font-semibold">B</span>
                        ) : tool === 'italic' ? (
                          <span className="text-base italic">I</span>
                        ) : tool === 'underline' ? (
                          <span className="text-base underline">U</span>
                        ) : (
                          <Icon className="h-5 w-5" />
                        )}
                      </button>

                      {shouldRenderSeparator && (
                        <div
                          aria-hidden="true"
                          className="mx-1 h-6 w-px bg-[#2a2a2a]"
                        />
                      )}
                    </React.Fragment>
                  )
                })}
              </div>

              <div className="flex w-1/2 items-center gap-1.5">
                {onPickFiles ? (
                  <BaseFileUpload
                    accept={pickFilesAccept}
                    multiple={pickFilesMultiple}
                    onFiles={onPickFiles}
                    disabled={disabled}
                    className="h-10 w-10 rounded-full border border-[#2a2a2a] border-solid bg-[#151515] p-0 text-foreground/70 hover:bg-[#1f1f1f] hover:text-foreground"
                    label={
                      <>
                        <Plus className="h-5 w-5" aria-hidden="true" />
                        <span className="sr-only">Adicionar anexo</span>
                      </>
                    }
                  />
                ) : (
                  <button
                    type="button"
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#2a2a2a] bg-[#151515] text-foreground/70 opacity-60"
                    aria-label="Adicionar anexo"
                    tabIndex={slackFormatOpen ? -1 : 0}
                    disabled
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    if (disabled || !isEditorReady) return
                    setSlackFormatOpen(true)
                  }}
                  className="inline-flex h-10 items-center justify-center rounded-full px-2 text-sm font-medium text-foreground/60 hover:bg-[#1f1f1f] hover:text-foreground"
                  aria-label="Abrir formatação"
                  title="Formatação"
                  tabIndex={slackFormatOpen ? -1 : 0}
                  disabled={!!disabled || !isEditorReady}
                >
                  Aa
                </button>
                <div className="relative inline-flex">
                  <button
                    type="button"
                    className={cn(
                      'inline-flex h-10 w-10 items-center justify-center rounded-full text-foreground/60 hover:bg-[#1f1f1f] hover:text-foreground',
                      emojiPickerOpen && isLg && 'bg-[#1f1f1f] text-foreground',
                    )}
                    aria-label="Emoji"
                    title={renderEmojiPicker ? 'Emoji' : 'Em breve'}
                    aria-expanded={emojiPickerOpen ? 'true' : 'false'}
                    tabIndex={slackFormatOpen ? -1 : 0}
                    disabled={!!disabled || !isEditorReady || !renderEmojiPicker}
                    onClick={handleEmojiButtonClick}
                    ref={emojiButtonRef}
                  >
                    <Smile className="h-5 w-5" />
                  </button>
                </div>
                <button
                  type="button"
                  className={cn(
                    'inline-flex h-10 w-10 items-center justify-center rounded-full text-foreground/60 hover:bg-[#1f1f1f] hover:text-foreground',
                    mentionOpen && 'bg-[#1f1f1f] text-foreground',
                  )}
                  aria-label="Mencionar"
                  title={mentionsEnabled ? 'Mencionar' : 'Em breve'}
                  aria-expanded={mentionOpen ? 'true' : 'false'}
                  tabIndex={slackFormatOpen ? -1 : 0}
                  disabled={!!disabled || !isEditorReady || !mentionsEnabled}
                  onClick={handleMentionButtonClick}
                  ref={mentionButtonRef}
                >
                  <AtSign className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          {(footerRightSlot || (shouldShowSubmitButton && onSubmit)) && (
            <div className="flex items-center gap-2">
              {footerRightSlot}
              {shouldShowSubmitButton && onSubmit && (
                <button
                  type="button"
                  onClick={() => {
                    if (!resolvedSubmitDisabled) onSubmit()
                  }}
                  className={cn(
                    'inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#2a2a2a] bg-[#151515] text-foreground/80 hover:bg-[#1f1f1f] hover:text-foreground',
                    resolvedSubmitDisabled && 'cursor-not-allowed opacity-50 hover:bg-[#151515]',
                  )}
                  aria-label={submitLabel || 'Enviar'}
                  disabled={resolvedSubmitDisabled}
                >
                  <SendHorizontal className="h-5 w-5" />
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        tools.length > 0 && (
          <div className="mt-1 flex items-center justify-between text-xs text-foreground/70">
            <div className="flex items-center gap-1">
              {tools.map((tool) => {
                const cfg = TOOL_CONFIG[tool]
                if (!cfg) return null
                const Icon = cfg.icon
                const toolDisabled =
                  !!disabled || !isEditorReady || !isToolSupported(tool)
                return (
                  <button
                    key={tool}
                    type="button"
                    onClick={() => handleToolClick(tool)}
                    className={cn(
                      'inline-flex h-7 w-7 items-center justify-center rounded-md text-[11px] text-foreground/80 hover:bg-[#1e293b] hover:text-foreground',
                      toolDisabled && 'cursor-not-allowed opacity-40',
                    )}
                    aria-label={cfg.label}
                    disabled={toolDisabled}
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </button>
                )
              })}
            </div>
            {(footerRightSlot || (shouldShowSubmitButton && onSubmit)) && (
              <div className="flex items-center gap-2">
                {footerRightSlot}
                {shouldShowSubmitButton && onSubmit && (
                  <button
                    type="button"
                    onClick={() => {
                      if (!resolvedSubmitDisabled) onSubmit()
                    }}
                    className="inline-flex h-8 items-center justify-center rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
                    disabled={resolvedSubmitDisabled}
                  >
                    {submitLabel || 'Enviar'}
                  </button>
                )}
              </div>
            )}
          </div>
        )
      )}

      {variant === 'slack' && renderEmojiPicker && !isLg && (
        <BottomDrawer
          open={emojiPickerOpen}
          onOpenChange={setEmojiPickerOpen}
          header="Emojis"
          className="h-[50vh] max-h-[50vh] bg-[#111111]"
          zIndexClass="z-[180000]"
          overlayClassName="bg-black/60 backdrop-blur-sm"
        >
          {renderEmojiPicker({
            onSelect: (emoji) => {
              insertText(emoji)
              setEmojiPickerOpen(false)
            },
            onClose: () => setEmojiPickerOpen(false),
            className: 'w-full',
          })}
        </BottomDrawer>
      )}

      {variant === 'slack' &&
        renderEmojiPicker &&
        isLg &&
        emojiPortal &&
        emojiPickerOpen &&
        emojiPickerPosition &&
        createPortal(
          <div
            ref={emojiPopoverRef}
            className={cn(
              'fixed z-[180000]',
              emojiPickerAlign === 'right' ? 'text-right' : 'text-left',
            )}
            style={emojiPickerPosition}
            onMouseDown={(event) => event.stopPropagation()}
          >
            {renderEmojiPicker({
              onSelect: (emoji) => {
                insertText(emoji)
                setEmojiPickerOpen(false)
              },
              onClose: () => setEmojiPickerOpen(false),
              className: undefined,
            })}
          </div>,
          emojiPortal,
        )}

      {variant === 'slack' &&
        mentionsEnabled &&
        emojiPortal &&
        mentionOpen &&
        mentionPickerPosition &&
        createPortal(
          <div
            ref={mentionPopoverRef}
            className={cn(
              'fixed z-[180000]',
              mentionPickerAlign === 'right' ? 'text-right' : 'text-left',
            )}
            style={mentionPickerPosition}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <MentionAutocomplete
              items={mentionItems}
              activeIndex={mentionActiveIndex}
              loading={mentionLoading}
              hasMore={mentionHasMore}
              loadingMore={mentionLoadingMore}
              emptyLabel={mentionError || undefined}
              query={mentionQuery}
              onQueryChange={handleMentionQueryChange}
              onRequestClose={closeMention}
              onMoveActiveIndex={moveMentionActiveIndex}
              onSelectActive={selectActiveMention}
              onHoverIndex={(idx) => setMentionActiveIndex(idx)}
              onLoadMore={loadMoreMentions}
              onSelect={(user) => insertMention(user)}
              className="w-full"
            />
          </div>,
          emojiPortal,
        )}
    </div>
  )
}
